import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useFirebase } from '../contexts/FirebaseContext'
import {
  ShoppingCart,
  Search,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  X,
  User
} from 'lucide-react'
import { SkeletonTable } from '../components/Skeleton'
import toast from 'react-hot-toast'

const Purchases = () => {
  const navigate = useNavigate()
  const [purchases, setPurchases] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [purchasesPerPage] = useState(15)
  const [selectedUser, setSelectedUser] = useState(null)
  const [showModal, setShowModal] = useState(false)

  const { getAllUserPurchases } = useFirebase()

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      console.log('🔄 Loading all user purchases...')
      const purchasesData = await getAllUserPurchases()
      console.log('📊 Purchases data received:', purchasesData)
      setPurchases(purchasesData || [])
    } catch (error) {
      console.error('❌ Error loading purchases:', error)
      toast.error('Failed to load purchases')
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (date) => {
    if (!date) return 'N/A'
    try {
      let dateObj
      if (date.toDate && typeof date.toDate === 'function') {
        dateObj = date.toDate()
      } else if (date.seconds) {
        dateObj = new Date(date.seconds * 1000)
      } else {
        dateObj = new Date(date)
      }

      if (isNaN(dateObj.getTime())) {
        return 'Invalid Date'
      }

      return dateObj.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch (error) {
      console.error('Error formatting date:', error)
      return 'Invalid Date'
    }
  }

  // Filter purchases
  const filteredPurchases = purchases.filter(purchase => {
    if (!searchTerm) return true
    const searchLower = searchTerm.toLowerCase()
    return (
      purchase.userEmail?.toLowerCase().includes(searchLower) ||
      purchase.itemName?.toLowerCase().includes(searchLower) ||
      purchase.itemId?.toLowerCase().includes(searchLower) ||
      purchase.purchaseType?.toLowerCase().includes(searchLower) ||
      purchase.paymentStatus?.toLowerCase().includes(searchLower)
    )
  })

  // Sort by timestamp descending
  const sortedPurchases = filteredPurchases.sort((a, b) => {
    const dateA = a.timestamp?.toDate?.() || new Date(a.timestamp) || new Date(0)
    const dateB = b.timestamp?.toDate?.() || new Date(b.timestamp) || new Date(0)
    return new Date(dateB) - new Date(dateA)
  })

  // Pagination
  const indexOfLastPurchase = currentPage * purchasesPerPage
  const indexOfFirstPurchase = indexOfLastPurchase - purchasesPerPage
  const currentPurchases = sortedPurchases.slice(indexOfFirstPurchase, indexOfLastPurchase)
  const totalPages = Math.ceil(sortedPurchases.length / purchasesPerPage)

  const handleSearch = () => {
    setCurrentPage(1)
  }

  const clearSearch = () => {
    setSearchTerm('')
    setCurrentPage(1)
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Success':
        return <CheckCircle className="w-5 h-5 text-green-600" />
      case 'Pending':
        return <Clock className="w-5 h-5 text-yellow-600" />
      case 'Failed':
        return <XCircle className="w-5 h-5 text-red-600" />
      default:
        return <Clock className="w-5 h-5 text-gray-600" />
    }
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Success':
        return 'bg-green-100 text-green-800'
      case 'Pending':
        return 'bg-yellow-100 text-yellow-800'
      case 'Failed':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const handleUserClick = (userEmail) => {
    const userPurchases = purchases.filter(p => p.userEmail === userEmail)
    setSelectedUser({ email: userEmail, purchases: userPurchases })
    setShowModal(true)
  }

  const closeModal = () => {
    setShowModal(false)
    setSelectedUser(null)
  }

  const calculateTotalAmount = (userPurchases) => {
    return userPurchases.reduce((total, purchase) => {
      const amount = parseFloat(purchase.amount) || 0
      return total + amount
    }, 0)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl shadow-sm mb-2">
        <div className="absolute inset-0 bg-gradient-to-r from-amber-400 via-orange-500 to-orange-600" />
        <div className="relative flex items-center justify-between px-6 py-5">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/users')}
              className="p-2 bg-white/15 hover:bg-white/25 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                <ShoppingCart className="w-8 h-8 text-white" />
                User Purchases
              </h1>
              <p className="text-white/90 ml-11">Manage and track all user purchase history</p>
            </div>
          </div>
          <div className="text-right text-white">
            <div className="text-3xl font-bold">{purchases.length}</div>
            <div className="text-sm opacity-90">Total Purchases</div>
          </div>
        </div>
      </div>

      {/* Search Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by email, item name, ID, type, or payment status..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value)
                setCurrentPage(1)
              }}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <button
            onClick={clearSearch}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Purchases Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Purchases ({sortedPurchases.length})
          </h2>
        </div>

        {loading ? (
          <SkeletonTable rows={10} columns={2} />
        ) : sortedPurchases.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      User Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Date
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {currentPurchases.map((purchase, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleUserClick(purchase.userEmail)}
                          className="text-sm font-medium text-indigo-600 hover:text-indigo-800 hover:underline transition-colors"
                        >
                          {purchase.userEmail || 'N/A'}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          {formatDate(purchase.timestamp)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
                <div className="text-sm text-gray-600">
                  Showing {indexOfFirstPurchase + 1} to {Math.min(indexOfLastPurchase, sortedPurchases.length)} of {sortedPurchases.length}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="p-2 border border-gray-300 rounded-lg hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <div className="flex items-center gap-2">
                    {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                      const pageNum = currentPage > 3 ? currentPage - 2 + i : i + 1
                      if (pageNum > totalPages) return null
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`w-10 h-10 rounded-lg transition-colors ${
                            currentPage === pageNum
                              ? 'bg-indigo-600 text-white'
                              : 'border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      )
                    })}
                  </div>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="p-2 border border-gray-300 rounded-lg hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="p-12 text-center">
            <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <ShoppingCart className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No purchases found</h3>
            <p className="text-gray-600">
              {searchTerm ? `No purchases match "${searchTerm}"` : 'No purchase records available yet'}
            </p>
            {searchTerm && (
              <button
                onClick={clearSearch}
                className="mt-4 px-4 py-2 text-indigo-600 hover:text-indigo-700 font-medium"
              >
                Clear search
              </button>
            )}
          </div>
        )}
      </div>

      {/* User Purchase Details Modal */}
      {showModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3 text-white">
                <div className="bg-white bg-opacity-20 p-2 rounded-lg">
                  <User className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-xl font-bold">Purchase History</h2>
                  <p className="text-sm text-indigo-100">{selectedUser.email}</p>
                </div>
              </div>
              <button
                onClick={closeModal}
                className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
                  <div className="text-sm text-blue-600 font-medium mb-1">Total Purchases</div>
                  <div className="text-2xl font-bold text-blue-900">{selectedUser.purchases.length}</div>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
                  <div className="text-sm text-green-600 font-medium mb-1">Total Amount</div>
                  <div className="text-2xl font-bold text-green-900">₹{calculateTotalAmount(selectedUser.purchases).toFixed(2)}</div>
                </div>
              </div>

              {/* Purchases List */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">All Purchases</h3>
                {selectedUser.purchases
                  .sort((a, b) => {
                    const dateA = a.timestamp?.toDate?.() || new Date(a.timestamp) || new Date(0)
                    const dateB = b.timestamp?.toDate?.() || new Date(b.timestamp) || new Date(0)
                    return new Date(dateB) - new Date(dateA)
                  })
                  .map((purchase, index) => (
                    <div
                      key={index}
                      className="bg-gray-50 border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        {/* Purchase Type */}
                        <div>
                          <div className="text-xs text-gray-500 uppercase font-medium mb-1">Purchase Type</div>
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {purchase.purchaseType || 'N/A'}
                          </span>
                        </div>

                        {/* Amount */}
                        <div>
                          <div className="text-xs text-gray-500 uppercase font-medium mb-1">Amount</div>
                          <div className="text-lg font-bold text-indigo-700">
                            ₹{purchase.amount || 'N/A'}
                          </div>
                        </div>

                        {/* Payment Status */}
                        <div>
                          <div className="text-xs text-gray-500 uppercase font-medium mb-1">Status</div>
                          <div className="flex items-center gap-2">
                            {getStatusIcon(purchase.paymentStatus)}
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(purchase.paymentStatus)}`}>
                              {purchase.paymentStatus || 'N/A'}
                            </span>
                          </div>
                        </div>

                        {/* Date */}
                        <div>
                          <div className="text-xs text-gray-500 uppercase font-medium mb-1">Date</div>
                          <div className="flex items-center gap-2 text-sm text-gray-700">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            {formatDate(purchase.timestamp)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={closeModal}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Purchases
